import { theme } from '@contrast-security-inc/design-system-foundations'
import { css, cx } from '@emotion/css'
import _flatten from 'lodash/flatten'
import React, { useEffect, useState } from 'react'
import { FormattedMessage, useIntl, WrappedComponentProps } from 'react-intl'
import { usePopper } from 'react-popper'
import { CSSTransition } from 'react-transition-group'
import { Filter, FilterConfig } from '.'
import { HorizontalRule } from '../horizontal-rule'
import {
  buildModifiers,
  cssTransitionTimeouts,
  fadeCss,
  transitionName
} from '../popper'
import { SearchTextInput } from '../search-text'
import { getEnabledSubfilters, optionHasSubfilters } from './filter'
import { Option } from './index'
import {
  emptyStateFilterI18nConfig,
  filterOptionBottomSpacing,
  filterSectionSpacing,
  isEmptyStateFilter
} from './shared'
export interface FilterGroups {
  readonly [key: string]: string[]
}

interface OnMultiGroupToggleProps {
  readonly groupsOfAvailableOptions: Map<string, string[]>
}

interface OnGroupToggleProps {
  readonly groupID: string
  readonly optionsAvailableForSelection: string[]
}

interface OnOptionToggleProps {
  readonly groupID: string
  readonly optionID: string
}

export interface FilterConfigs {
  readonly [key: string]: FilterConfig
}

export interface FilterSelections {
  readonly [key: string]: string[]
}

const legacyHeaderHeight = 50
const legacyDrilldownHeaderHeight = 235
const gridHeaderHeight = 100
const columnRowHeight = 50

const offset = legacyHeaderHeight + gridHeaderHeight + columnRowHeight
const drilldownOffset =
  legacyDrilldownHeaderHeight + gridHeaderHeight + columnRowHeight

const popperContainerCss = cx(
  fadeCss,
  css`
    background-color: ${theme.color.white};
    border: 1px solid ${theme.color.neutral40};
    padding: 0;
    border-radius: 3px;
    box-shadow: 0 0 4px rgba(0, 0, 0, 0.15);
    outline: none;
    max-height: calc(100vh - ${offset}px);
    max-width: 315px;
    min-width: 225px;
  `
)

const drilldownPopperContainerCss = cx(
  popperContainerCss,
  css`
    max-height: calc(100vh - ${drilldownOffset}px);
  `
)

const filtersSearchInputCss = css`
  padding: 13px 15px 13px 19px;
  min-width: 225px;
  width: 100%;
  border-bottom: 1px solid ${theme.color.neutral40};
  input::placeholder {
    font-style: italic;
  }
  input {
    padding: 1px; /* fix cropped font descenders in FF */
    &:focus::placeholder {
      opacity: 0.5;
    }
  }
`

const searchIconClassName = css`
  color: ${theme.color.neutral60};
`

const scrollContainerOffset = offset + 50
const drilldownScrollContainerOffset = drilldownOffset + 50

const scrollContainerCss = css`
  overflow-y: auto;
  overflow-x: visible;
  max-height: calc(100vh - ${scrollContainerOffset}px);

  > ul:last-child {
    padding-bottom: ${filterSectionSpacing - filterOptionBottomSpacing}px;
  }
`

const drilldownScrollContainerCss = cx(
  scrollContainerCss,
  css`
    max-height: calc(100vh - ${drilldownScrollContainerOffset}px);
  `
)

interface FiltersChildrenProps {
  readonly isOpen: boolean
  readonly isTransitioning: boolean
}

export interface FiltersProps {
  readonly groupConfigs: FilterConfigs
  readonly selections?: FilterSelections
  readonly groupIDs: string[]
  readonly onMultiGroupToggle?: (props: OnMultiGroupToggleProps) => void
  readonly onGroupToggle: (props: OnGroupToggleProps) => void
  readonly onOptionToggle: (props: OnOptionToggleProps) => void
  readonly children: (props: FiltersChildrenProps) => React.ReactNode
  readonly isClickable?: boolean
  readonly e2eTestId?: string
}

function ScrollContainer({ children, isDrilldown }) {
  return (
    <div
      data-testid="filters"
      className={isDrilldown ? drilldownScrollContainerCss : scrollContainerCss}
      children={children}
    />
  )
}

interface FiltersSearchManagerChildrenProps {
  readonly updateSearchText: (searchText: string) => void
  readonly filtered: FilterConfigs
  readonly searchText?: string
}

interface FiltersSearchManagerProps {
  readonly children: (
    props: FiltersSearchManagerChildrenProps
  ) => React.ReactElement
  readonly groupIDs: string[]
  readonly groupConfigs: FilterConfigs
  readonly selections?: FilterSelections
}

function getFilteredConfig({
  groupIDs,
  groupConfigs,
  selections,
  intl,
  searchText
}) {
  return !searchText
    ? groupConfigs
    : groupIDs.reduce((acc, groupID) => {
        const config = groupConfigs[groupID]
        const hasOptions = config && config.options && config.options.length

        return {
          ...acc,
          [groupID]: {
            ...config,
            options: hasOptions
              ? config.options.filter((opt) => {
                  // keep options that match search text
                  const searchMatch = isEmptyStateFilter(
                    config.optionToString(opt, intl)
                  )
                    ? intl
                        .formatMessage({
                          id: emptyStateFilterI18nConfig[
                            config.optionToString(opt, intl)
                          ]
                        })

                        .indexOf(searchText) !== -1
                    : config
                        .optionToString(opt, intl)

                        .indexOf(searchText) !== -1

                  // keep options that are selected
                  const isSelected =
                    selections &&
                    selections[groupID] &&
                    selections[groupID].find(
                      (selection) => selection === config.optionToID(opt)
                    )
                  return searchMatch || isSelected
                })
              : []
          }
        }
      }, {})
}

function FiltersSearchManager({
  groupIDs,
  groupConfigs,
  selections,
  intl,
  children
}: FiltersSearchManagerProps & WrappedComponentProps) {
  const [searchText, setSearchText] = useState('')

  return children({
    updateSearchText: setSearchText,
    filtered: getFilteredConfig({
      groupIDs,
      groupConfigs,
      selections,
      intl,
      searchText
    }),
    searchText
  })
}

const noFiltersCss = css`
  label: contrast-no-filters-message;
  padding: ${filterSectionSpacing}px 0;
  text-align: center;
  color: ${theme.color.neutral50};
`
function NoFilters() {
  return (
    <FormattedMessage
      id="detail_noResultsFound"
      defaultMessage="No results found"
    >
      {(text) => <div className={noFiltersCss} children={text} />}
    </FormattedMessage>
  )
}

function FiltersTextSearch({ updateSearchText }) {
  return (
    <SearchTextInput
      placeholderI18n={{
        id: 'placeholder_searchFilters',
        defaultMessage: 'Search filters...'
      }}
      onChange={(val) => updateSearchText(val)}
      className={filtersSearchInputCss}
      searchIconClassName={searchIconClassName}
      alwaysShowPlaceholder
    />
  )
}

interface FilterGroupProps {
  readonly searchText?: string
  readonly group: FilterConfig
  readonly selections?: FilterSelections
  readonly onOptionToggle: (props: OnOptionToggleProps) => void
  readonly onGroupToggle: (props: OnGroupToggleProps) => void
  readonly onMultiGroupToggle?: (props: OnMultiGroupToggleProps) => void
}

function FilterGroup({
  searchText,
  group,
  selections,
  onGroupToggle,
  onMultiGroupToggle,
  onOptionToggle
}: FilterGroupProps) {
  return (
    <Filter
      searchText={searchText}
      group={group}
      selections={groupFiltersAndSubfilters(selections, group)}
      onMultiGroupToggle={() => {
        if (onMultiGroupToggle) {
          const groupsOfAvailableOptions = buildGroupsOfAvailableOptions(group)
          onMultiGroupToggle({ groupsOfAvailableOptions })
        }
      }}
      onGroupToggle={onGroupToggle}
      onOptionToggle={onOptionToggle}
    />
  )
}

// returns a map with groupID as the key and enabled option keycodes for the value
export const buildGroupsOfAvailableOptions = (
  group: FilterConfig
): Map<string, string[]> => {
  const groupsOfAvailableOptions = new Map<string, string[]>()
  const optionsWithoutSubfilters = group.options
    .filter((option) => !optionHasSubfilters(option) && option.count)
    .map((option) => group.optionToID(option))

  if (optionsWithoutSubfilters.length > 0) {
    groupsOfAvailableOptions.set(group.id, optionsWithoutSubfilters)
  }

  const optionsWithEnabledSubfilters: Option[] = group.options.filter(
    (option) =>
      optionHasSubfilters(option) && getEnabledSubfilters(option).length > 0
  )

  if (optionsWithEnabledSubfilters.length > 0) {
    optionsWithEnabledSubfilters.forEach((option) => {
      const subfilterId = option.key.subfiltersId
      const enabledSubfilterKeycodes = getEnabledSubfilters(option).map(
        (subfilter) => subfilter.keycode
      )

      groupsOfAvailableOptions.set(subfilterId, enabledSubfilterKeycodes)
    })
  }
  return groupsOfAvailableOptions
}

export const groupFiltersAndSubfilters = (selections, group): string[] => {
  const groupID = group.id
  const optionsWithSubfilters = group.options.filter(
    (option) => option?.key?.subfilters
  )
  const filteredSelections: string[] = []

  optionsWithSubfilters.forEach((option) => {
    const subfilterId = option.key.subfiltersId
    const isSubfilterSelected = Object.keys(selections).includes(subfilterId)
    if (isSubfilterSelected) {
      // add subfilters
      filteredSelections.push(selections[subfilterId])
    }
  })
  if (selections[groupID]) {
    // add filters
    filteredSelections.push(selections[groupID])
  }
  return _flatten(filteredSelections)
}

const hrCss = css`
  margin: ${filterSectionSpacing - filterOptionBottomSpacing}px 0
    ${filterSectionSpacing}px 0;
  padding-bottom: 0;
`

function FilterGroupRule() {
  return <HorizontalRule className={hrCss} />
}

const focusDelay = 100

type TooltipElement = HTMLElement | null

export const Filters = (
  props: FiltersProps & {
    readonly children: (props: {
      readonly isOpen: boolean
      readonly isTransitioning: boolean
    }) => React.ReactElement
    readonly isDrilldown?: boolean
  }
) => {
  const {
    children,
    groupIDs,
    groupConfigs,
    selections,
    onGroupToggle,
    onOptionToggle,
    onMultiGroupToggle,
    isDrilldown = false,
    isClickable = true,
    e2eTestId
  } = props

  const intl = useIntl()
  const [isOpen, setIsOpen] = useState(false)
  const [isTransitioning, setIsTransitioning] = useState(false)

  const [referenceElement, setReferenceElement] = useState<TooltipElement>(null)
  const [popperElement, setPopperElement] = useState<TooltipElement>(null)

  const {
    styles,
    attributes: { popper: popperAttributes }
  } = usePopper(referenceElement, popperElement, {
    placement: 'bottom-start',
    modifiers: buildModifiers(0, [{ name: 'flip', enabled: false }])
  })

  const [doFocusPopper, setDoFocusPopper] = useState(false)
  useEffect(() => {
    if (doFocusPopper) {
      const timer = setTimeout(() => {
        setDoFocusPopper(false)
        if (popperElement) {
          popperElement.focus()
        }
        return () => clearTimeout(timer)
      }, focusDelay)
    }
  }, [doFocusPopper, popperElement])

  const toggle = () => {
    // Prevent user from toggling while a toggle transition is active
    // Does not seem to be a problem for hover interaction
    if (isTransitioning) {
      return
    }
    if (!isOpen) {
      setDoFocusPopper(true)
    }
    setIsOpen(!isOpen)
  }

  const onBlur = (e: React.FocusEvent<HTMLSpanElement>) => {
    // IE11 does not support relatedTarget, but according to
    // https://stackoverflow.com/a/49325196/300575 if we capture the
    // document activeElement when relatedTarget is null, this works.
    const relatedTarget =
      e.relatedTarget === null ? document.activeElement : e.relatedTarget
    if (
      document.activeElement !== popperElement &&
      (!relatedTarget ||
        (relatedTarget instanceof HTMLElement &&
          !e.currentTarget.contains(relatedTarget)))
    ) {
      setIsOpen(!isOpen)
    }
  }

  const preventOnBlurAfterOnClick = (
    e: React.SyntheticEvent<HTMLSpanElement>
  ) => {
    e.stopPropagation()
    e.preventDefault()
  }

  const hasGroups = groupIDs && groupIDs.length
  const myGroupConfigs = groupIDs.reduce((acc, curr) => {
    return {
      ...acc,
      [curr]: groupConfigs[curr]
    }
  }, {})

  return (
    <>
      <span
        ref={setReferenceElement}
        onClick={isClickable ? toggle : () => undefined}
        onMouseDown={preventOnBlurAfterOnClick}
      >
        {children({ isOpen, isTransitioning })}
      </span>
      <CSSTransition
        unmountOnExit
        in={isOpen}
        timeout={cssTransitionTimeouts}
        classNames={transitionName}
        appear={true}
        onEnter={() => setIsTransitioning(true)}
        onEntered={() => setIsTransitioning(false)}
        onExit={() => setIsTransitioning(true)}
        onExited={() => setIsTransitioning(false)}
      >
        <span
          data-e2e={e2eTestId}
          ref={setPopperElement}
          style={styles.popper}
          onBlur={onBlur}
          className={
            isDrilldown ? drilldownPopperContainerCss : popperContainerCss
          }
          tabIndex={0}
          {...popperAttributes}
        >
          <FiltersSearchManager
            groupIDs={groupIDs}
            groupConfigs={myGroupConfigs}
            selections={selections}
            intl={intl}
          >
            {({ updateSearchText, filtered, searchText }) => {
              let groupsWithOptionsCount = 0
              let prevHasOptions = false

              return (
                <React.Fragment>
                  <FiltersTextSearch updateSearchText={updateSearchText} />
                  <div
                    className={css`
                      position: relative;
                    `}
                  >
                    <ScrollContainer isDrilldown={isDrilldown}>
                      {!!hasGroups &&
                        Object.keys(filtered).map(
                          (groupID: string, groupIndex) => {
                            const group = filtered[groupID]
                            const hasOptions = !!(
                              group &&
                              group.options &&
                              group.options.length > 0
                            )
                            if (hasOptions) {
                              groupsWithOptionsCount++
                            }
                            const first = groupIndex === 0
                            const showRule =
                              !first && hasOptions && prevHasOptions

                            const response = hasOptions && (
                              <React.Fragment key={groupIndex}>
                                {showRule && <FilterGroupRule />}
                                {hasOptions && (
                                  <FilterGroup
                                    searchText={searchText}
                                    group={group}
                                    selections={selections}
                                    onOptionToggle={onOptionToggle}
                                    onMultiGroupToggle={onMultiGroupToggle}
                                    onGroupToggle={onGroupToggle}
                                  />
                                )}
                              </React.Fragment>
                            )

                            prevHasOptions = hasOptions || prevHasOptions
                            return response
                          }
                        )}
                      {!groupsWithOptionsCount && <NoFilters />}
                    </ScrollContainer>
                  </div>
                </React.Fragment>
              )
            }}
          </FiltersSearchManager>
        </span>
      </CSSTransition>
    </>
  )
}
